import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { CubeGrid } from '../components/ui/CountingCubes';
import { BLOCKS, getLessonById, getLessonsByBlock } from '../data/curriculum';
import { 
  PlayCircle, 
  Flame, 
  Trophy, 
  ChevronRight,
  BarChart3,
  Sparkles
} from 'lucide-react';

const HomePage = ({ progress, isLessonUnlocked, isLessonCompleted, getLessonAccuracy }) => {
  const navigate = useNavigate();
  
  const currentLesson = getLessonById(progress.currentLessonId);
  const currentBlock = currentLesson ? BLOCKS.find(b => b.id === currentLesson.blockId) : BLOCKS[0];
  
  // Calculate overall progress
  const totalLessons = 15;
  const completedCount = progress.completedLessons.length;
  const overallProgress = Math.round((completedCount / totalLessons) * 100);

  // Get lessons for current block
  const blockLessons = currentBlock ? getLessonsByBlock(currentBlock.id) : [];
  const blockCompletedLessons = blockLessons.filter(l => isLessonCompleted(l.id)).length;

  const handleStartLesson = () => {
    navigate(`/lesson/${progress.currentLessonId}`);
  };

  return (
    <div className="app-container min-h-screen bg-background px-5 py-6 safe-area-top safe-area-bottom">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between mb-6"
      >
        <div>
          <p className="text-sm text-muted-foreground">Привет, {progress.parentName}!</p>
          <h1 className="text-xl font-display font-bold text-foreground">
            Занятия для {progress.childName}
          </h1>
        </div>
        
        {/* Streak indicator */}
        {progress.streakDays > 0 && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-secondary-light"
          >
            <Flame className="w-4 h-4 text-secondary" />
            <span className="text-sm font-bold text-secondary-foreground">{progress.streakDays}</span>
          </motion.div>
        )}
      </motion.div>

      {/* Current Lesson Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="lesson-card mb-6"
      >
        {/* Block indicator */}
        <div className="flex items-center gap-2 mb-4">
          <div className={`w-8 h-8 rounded-lg bg-${currentBlock?.color || 'cube-1'} flex items-center justify-center`} 
               style={{ background: `hsl(var(--${currentBlock?.color || 'cube-1'}))` }}>
            <Sparkles className="w-4 h-4 text-card" />
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Блок {currentBlock?.id}</p>
            <p className="text-sm font-medium">{currentBlock?.title}</p>
          </div>
        </div>

        {/* Lesson info */}
        <div className="mb-4">
          <h2 className="text-lg font-display font-bold text-foreground mb-1">
            Урок {currentLesson?.id}: {currentLesson?.title}
          </h2>
          <p className="text-sm text-muted-foreground">
            {currentLesson?.description}
          </p>
        </div>

        {/* Block progress */}
        <div className="mb-5">
          <div className="flex justify-between text-xs text-muted-foreground mb-2">
            <span>Прогресс блока</span>
            <span>{blockCompletedLessons}/{blockLessons.length}</span>
          </div>
          <Progress 
            value={(blockCompletedLessons / blockLessons.length) * 100} 
            className="h-2"
          />
        </div>

        {/* Start button */}
        <Button
          onClick={handleStartLesson}
          className="btn-child-primary w-full flex items-center justify-center gap-2"
        >
          <PlayCircle className="w-5 h-5" />
          Начать урок
        </Button>
      </motion.div>

      {/* Stats Row */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="grid grid-cols-2 gap-3 mb-6"
      >
        {/* Completed lessons */}
        <div className="p-4 rounded-2xl bg-card border border-border">
          <div className="flex items-center gap-2 mb-2">
            <Trophy className="w-4 h-4 text-warning" />
            <span className="text-xs text-muted-foreground">Пройдено</span>
          </div>
          <p className="text-2xl font-display font-bold">{completedCount}</p>
          <p className="text-xs text-muted-foreground">уроков</p>
        </div>

        {/* Overall progress */}
        <div className="p-4 rounded-2xl bg-card border border-border">
          <div className="flex items-center gap-2 mb-2">
            <BarChart3 className="w-4 h-4 text-primary" />
            <span className="text-xs text-muted-foreground">Прогресс</span>
          </div>
          <p className="text-2xl font-display font-bold">{overallProgress}%</p>
          <p className="text-xs text-muted-foreground">выполнено</p>
        </div>
      </motion.div>

      {/* View progress button */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <Button
          onClick={() => navigate('/progress')}
          variant="outline"
          className="w-full flex items-center justify-between h-14 rounded-xl"
        >
          <span className="flex items-center gap-2">
            <CubeGrid count={3} size="sm" animate={false} />
            <span>Все уроки</span>
          </span>
          <ChevronRight className="w-5 h-5 text-muted-foreground" />
        </Button>
      </motion.div>

      {/* Daily tip */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="mt-6 p-4 rounded-2xl bg-primary-light"
      >
        <p className="text-sm text-foreground">
          <span className="font-bold">Совет:</span> Занимайтесь каждый день по 3-7 минут. 
          Короткие регулярные занятия эффективнее долгих!
        </p>
      </motion.div>
    </div>
  );
};

export default HomePage;
